import { useCallback, useMemo } from 'react';
import { Box, Link, Stack, Typography } from '@mui/material';
import {
  DataGridPremium,
  type GridColDef,
  type GridListViewColDef,
  type GridRenderCellParams,
  type GridSortModel,
} from '@mui/x-data-grid-premium';
import { useLocalization } from '@/hooks/useLocalization';
import type {
  InvestorNetWorthChange,
  NetWorthChangePeriod,
} from '@/interfaces/netWorthChanges';
import { formatNumberToCurrency } from '@/utils/currency';
import { formatAppDate } from '@/utils/date';
import NetWorthChangeValue from './NetWorthChangeValue';
import {
  formatDollarChange,
  formatPercentChange,
  isTotalRow,
  sortablePercentChange,
} from './netWorthChangeFormat';

/**
 * Centres a Name cell's content. Grid cells centre by line height, which an
 * inline link inherits but a block Typography -- the Total label, or a name
 * without the link -- overrides, leaving it at the top of the cell.
 */
const NAME_CELL_SX = { display: 'flex', alignItems: 'center', height: '100%' };

/**
 * Legacy's default order: % Change high to low, signed, so gains come first
 * and the deepest fall last (`DailyChangesGrid`'s `defaultSort`). Which ten
 * clients are shown is decided before this by `rankNetWorthChanges`, by the
 * size of the move either way; this only orders those ten on screen, and a
 * header click re-sorts them.
 */
const DEFAULT_SORT: GridSortModel = [{ field: 'PercentChange', sort: 'desc' }];

interface NetWorthChangesGridProps {
  /** The ranked, searched clients to show. */
  rows: InvestorNetWorthChange[];
  /** The pinned footer totalling `rows`. */
  totalRow: InvestorNetWorthChange;
  /** The dates the value columns are headed with. */
  period: NetWorthChangePeriod;
  /** Stacked cards rather than columns, for half width and mobile. */
  isCompact: boolean;
  /** Whether a name is a link; without access it is plain text. */
  canOpenClient: boolean;
  onOpenClient: (investorId: number) => void;
}

/**
 * The ranked clients as a grid: name, both dated values, and the dollar and
 * percentage change, with a pinned total. In compact mode each client is a
 * card instead, since five columns of currency do not fit.
 *
 * The grid is keyed on its mode and remounts when it changes, because
 * switching `listView` off on a mounted grid leaves its column headers and
 * cells empty.
 */
export default function NetWorthChangesGrid({
  rows,
  totalRow,
  period,
  isCompact,
  canOpenClient,
  onOpenClient,
}: NetWorthChangesGridProps) {
  const { t } = useLocalization();

  const renderName = useCallback(
    (row: InvestorNetWorthChange) => {
      if (isTotalRow(row)) {
        return <Typography sx={{ fontWeight: 'bold' }}>{row.Name}</Typography>;
      }
      if (!canOpenClient) {
        return (
          <Typography sx={{ overflowWrap: 'anywhere' }}>{row.Name}</Typography>
        );
      }
      return (
        <Link
          component='button'
          type='button'
          sx={{ textAlign: 'left', overflowWrap: 'anywhere' }}
          onClick={() => {
            onOpenClient(row.Id);
          }}
        >
          {row.Name}
        </Link>
      );
    },
    [canOpenClient, onOpenClient],
  );

  const startHeader = t('netWorthChanges.valueOn', '{{date}} Value', {
    date: formatAppDate(period.startDate),
  });
  const endHeader = t('netWorthChanges.valueOn', '{{date}} Value', {
    date: formatAppDate(period.endDate),
  });

  const columns: GridColDef<InvestorNetWorthChange>[] = useMemo(
    () => [
      {
        field: 'Name',
        headerName: t('name', 'Name'),
        flex: 1.6,
        minWidth: 160,
        renderCell: (params: GridRenderCellParams<InvestorNetWorthChange>) => (
          <Box sx={NAME_CELL_SX}>{renderName(params.row)}</Box>
        ),
      },
      {
        field: 'OldDollarBalance',
        headerName: startHeader,
        type: 'number',
        flex: 1,
        minWidth: 130,
        valueFormatter: (value: number) => formatNumberToCurrency(value),
      },
      {
        field: 'DollarBalance',
        headerName: endHeader,
        type: 'number',
        flex: 1,
        minWidth: 130,
        valueFormatter: (value: number) => formatNumberToCurrency(value),
      },
      {
        field: 'DollarChange',
        headerName: t('netWorthChanges.dollarChange', '$ Change'),
        type: 'number',
        flex: 1,
        minWidth: 130,
        renderCell: (params: GridRenderCellParams<InvestorNetWorthChange>) => (
          <NetWorthChangeValue
            value={params.row.DollarChange}
            text={formatDollarChange(params.row)}
          />
        ),
      },
      {
        field: 'PercentChange',
        headerName: t('netWorthChanges.percentChange', '% Change'),
        type: 'number',
        flex: 0.8,
        minWidth: 110,
        valueGetter: (_value, row: InvestorNetWorthChange) =>
          sortablePercentChange(row),
        renderCell: (params: GridRenderCellParams<InvestorNetWorthChange>) => (
          <NetWorthChangeValue
            value={params.row.PercentChange}
            text={formatPercentChange(params.row)}
            caret
          />
        ),
      },
    ],
    [t, renderName, startHeader, endHeader],
  );

  const listViewColumn: GridListViewColDef<InvestorNetWorthChange> = useMemo(
    () => ({
      field: 'listColumn',
      renderCell: (params) => {
        const row = params.row;
        return (
          <Box sx={{ width: '100%', py: 1.5 }}>
            <Stack
              direction='row'
              justifyContent='space-between'
              alignItems='center'
              spacing={1}
            >
              <Box sx={{ minWidth: 0, flex: 1 }}>{renderName(row)}</Box>
              <NetWorthChangeValue
                value={row.PercentChange}
                text={formatPercentChange(row)}
                caret
              />
            </Stack>
            <Stack
              direction='row'
              justifyContent='space-between'
              sx={{ mt: 0.5 }}
            >
              <Typography
                variant='body2'
                color='text.secondary'
              >
                {formatNumberToCurrency(row.DollarBalance)}
              </Typography>
              <NetWorthChangeValue
                value={row.DollarChange}
                text={formatDollarChange(row)}
              />
            </Stack>
          </Box>
        );
      },
    }),
    [renderName],
  );

  return (
    <DataGridPremium<InvestorNetWorthChange>
      key={isCompact ? 'list' : 'grid'}
      rows={rows}
      columns={columns}
      getRowId={(row) => row.Id}
      pinnedRows={{ bottom: [totalRow] }}
      initialState={{ sorting: { sortModel: DEFAULT_SORT } }}
      listView={isCompact}
      listViewColumn={listViewColumn}
      getRowHeight={() => (isCompact ? 'auto' : null)}
      disableRowSelectionOnClick
      disableColumnMenu
      disableColumnFilter
      disableColumnSelector
      hideFooter
      autoHeight
    />
  );
}
